import type { BlogPost } from '../../../../app/features/blog/blog.types';

type RawPost = Omit<BlogPost, 'readingTimeMinutes'>;

export const infraPosts: RawPost[] = [
  {
    slug: 'railway-trust-proxy',
    title: 'The throttler that never fired: Railway, trust proxy, and the hour I lost',
    description:
      'A short debugging story about rate limiting, ingress proxies, and why "trust proxy 1" is wrong on Railway.',
    date: '2026-04-08',
    content: `## The symptom

I had just hardened the API with \`@nestjs/throttler\`. The config looked fine. The route decorator looked fine. The response headers even showed \`x-ratelimit-limit: 5\` when hitting \`/auth/login\`. But no matter how many requests I fired, the 429 never came. Every call returned 401.

I bounced through three wrong explanations before I finally looked at the logs.

## The logs told me everything

Three consecutive requests to the same endpoint, from the same curl on the same machine, were showing up in Pino with different IPs:

\`\`\`
req {"id":3,"url":"/api/auth/login","ip":"::ffff:100.64.0.14"}
req {"id":4,"url":"/api/auth/login","ip":"::ffff:100.64.0.20"}
req {"id":5,"url":"/api/auth/login","ip":"::ffff:100.64.0.23"}
\`\`\`

Railway's ingress doesn't hand your container a single proxy hop. It hands you a fleet of internal gateway nodes, each with its own \`100.64.x.x\` address. Every request bounces through a different one. To Express, each request looked like a brand new client, so the throttler's per-IP bucket was always empty.

## The fix

I had set \`app.set('trust proxy', 1)\` — trust the first proxy in the chain. That's the recommended setting for a single-hop ingress like Vercel or Heroku. On Railway it's exactly wrong: \`1\` trusts only the first hop, so the IP Express reads is still whichever internal gateway the request bounced off, not the original client.

The correction is one character:

\`\`\`ts
// Before
app.set('trust proxy', 1);

// After — trust the full chain and read the first x-forwarded-for entry
app.set('trust proxy', true);
\`\`\`

With \`true\`, Express walks back to the start of \`x-forwarded-for\` and uses that as \`req.ip\`. The throttler suddenly saw the real client, accumulated hits, and fired the 429 on request six.

## The generalisation

Every time I've hit a rate limit bug in the last five years, the answer has been "you're behind a proxy you didn't know about". Cloudflare, Heroku, Fly, Railway — they all insert hops. The fix is always the same: look at what \`req.ip\` actually resolves to on the server, not what you think it should be.

If you're deploying a Nest or Express app on Railway today, add \`trust proxy\` to your setup. It takes ten seconds and it prevents an entire category of silent failures.
`,
  },
  {
    slug: 'csp-nonces-angular-ssr',
    title: 'CSP con nonces en Angular SSR: la guía que no encontré en ningún lado',
    description:
      'Cómo implementar Content Security Policy con nonces por petición en Angular SSR, incluido el gotcha de Beasties y el problema de style-src con Tailwind.',
    date: '2026-04-15',
    content: `## Por qué CSP importa de verdad

Content Security Policy no es un header que se agrega "para cumplir" en una auditoría. Es la última línea de defensa contra XSS. Si un atacante consigue inyectar un \`<script>\` en tu HTML —mediante una vulnerabilidad en el servidor, un paquete npm comprometido, o contenido de usuario mal sanitizado— CSP decide si ese script puede ejecutarse.

Sin CSP, un \`<script>alert(document.cookie)</script>\` en el DOM es una sesión robada. Con CSP estricto, es un error en la consola y una entrada en tu endpoint de reporting.

El modo más robusto es \`script-src 'nonce-{valor}'\`: cada script legítimo lleva un atributo \`nonce\` cuyo valor coincide con el del header. El valor cambia en cada petición, así que un atacante no puede pre-calcularlo.

La teoría es simple. La implementación en Angular SSR tiene tres trampas que no están documentadas juntas en ningún sitio.

## Trampa 1: Beasties y los inline event handlers

Angular SSR usa Beasties (antes critters) para incrustar el CSS crítico en el HTML inicial y evitar el parpadeo de estilos. El problema es que Beasties emite esto para cargar el resto del CSS de forma no bloqueante:

\`\`\`html
<link rel="stylesheet" href="/styles.css" media="print" onload="this.media='all'">
\`\`\`

El atributo \`onload="this.media='all'"\` es un inline event handler. Con una política \`script-src 'nonce-xyz'\` activa, el navegador lo bloquea. Las fuentes de la app no cargan, el layout se rompe, y en la consola aparece un error CSP que no menciona a Beasties en ningún lado.

La solución es post-procesar el HTML del servidor para reemplazar ese patrón antes de enviarlo al cliente. En el \`server.ts\` de este portfolio:

\`\`\`typescript
function postProcessHtml(html: string, nonce: string): string {
  // Reemplazar el inline handler de Beasties por un script nonce'd equivalente
  return html.replace(
    /onload="this.media='all'"/g,
    \`data-beasties-onload="this.media='all'"\`
  ).replace(
    /(<[/]body>)/,
    \`<script nonce="\${nonce}">
      document.querySelectorAll('link[data-beasties-onload]').forEach(function(el) {
        el.onload = function() { this.media = 'all'; };
      });
    </script></body>\`
  );
}
\`\`\`

El script con nonce se ejecuta, encuentra los elementos marcados con \`data-beasties-onload\`, y les asigna el handler por JavaScript —ya no es inline, ya no viola CSP.

## Trampa 2: generar y propagar el nonce

El nonce tiene que generarse en el servidor, inyectarse en el header CSP, y estar disponible para Angular a la hora de renderizar. En Express + Angular SSR:

\`\`\`typescript
import { randomBytes } from 'crypto';

app.use((req, res, next) => {
  const nonce = randomBytes(16).toString('base64');
  res.locals['nonce'] = nonce;

  res.setHeader(
    'Content-Security-Policy',
    [
      \`default-src 'self'\`,
      \`script-src 'self' 'nonce-\${nonce}'\`,
      \`style-src 'self' 'unsafe-inline'\`,  // ver trampa 3
      \`connect-src 'self' https://tu-api.railway.app\`,
      \`img-src 'self' data: https:\`,
      \`frame-ancestors 'none'\`,
    ].join('; ')
  );

  next();
});
\`\`\`

Para que Angular use el nonce en sus scripts de hidratación, hay que pasarlo mediante \`CSP_NONCE\` token de Angular:

\`\`\`typescript
// En el handler de SSR
const html = await commonEngine.render({
  bootstrap,
  documentFilePath: indexHtml,
  url: req.originalUrl,Path: browserDistFolder,
  providers: [
    { provide: APP_BASE_HREF, useValue: req.baseUrl },
    { provide: 'CSP_NONCE', useValue: res.locals['nonce'] },
  ],
});

res.send(postProcessHtml(html, res.locals['nonce'] as string));
\`\`\`

Angular 17+ lee automáticamente el token \`CSP_NONCE\` y lo agrega a todos sus scripts de hidratación internos.

## Trampa 3: style-src 'unsafe-inline' con Tailwind

Tailwind genera utilities CSS en archivos estáticos, no en estilos inline. En teoría, \`style-src 'self'\` debería ser suficiente. En la práctica, hay dos excepciones:

1. **Angular animations**: algunas animaciones de Angular inyectan estilos inline via \`Renderer2\`. Con \`style-src 'self'\` se bloquean.
2. **Third-party widgets**: cualquier librería que use \`element.style.xxx\` directamente viola la política si se incluye \`require-trusted-types-for 'script'\`.

La solución pragmática es \`style-src 'self' 'unsafe-inline'\`. Sí, debilita la protección contra CSS injection, pero CSS injection solo puede causar clickjacking y exfiltración de datos mediante selectores —no ejecución de código. Con \`script-src\` estricto y \`frame-ancestors 'none'\`, el vector de ataque más crítico está cerrado.

Si necesitás eliminar \`unsafe-inline\` de \`style-src\`, la alternativa es un hash por cada bloque de estilos inline que uses —pero con Angular eso es gestión manual de hashes en cada build, lo que no escala.

## Monitorizar violaciones en producción

Una política nueva inevitablemente bloquea algo que no anticipaste. El mecanismo para saberlo antes de que un usuario se queje es \`report-uri\` (o su sucesor \`report-to\`):

\`\`\`
Content-Security-Policy: ...; report-uri /api/csp-report
\`\`\`

\`\`\`typescript
// NestJS controller
@Post('csp-report')
@HttpCode(204)
reportCspViolation(@Body() report: unknown): void {
  this.logger.warn({ type: 'csp-violation', report });
}
\`\`\`

El navegador envía un JSON con la directiva violada, el recurso bloqueado, y la URL donde ocurrió. Con una semana de datos en producción, sabés exactamente qué falta añadir a la política antes de hacerla más restrictiva.

## El resultado

Con estas tres piezas en su lugar —nonce por petición, post-procesado de Beasties, y reporting de violaciones— este portfolio lleva semanas en producción sin una sola violación CSP legítima. Los únicos reportes que llegan son extensiones de Chrome que inyectan scripts propios, que no son problema nuestro.

Es trabajo que no se ve. Pero es la diferencia entre una app que confía en "nadie va a atacar esto" y una que asume que alguien va a intentarlo.
`,
  },
];
